// Online match client. Implements the netcode PROTOCOL exactly (see the build
// brief): commit-reveal over WebSocket, SHA-256 commit hashes, 30s reconnect
// grace, and optional Phantom wallet linking. The authoritative match Worker is
// out of this client's scope (Cloudflare Worker + Durable Object); this speaks
// its wire format and degrades gracefully when no server is reachable.

import { wsUrl, apiBase } from "./config";
import type { Move as RpsMove } from "../rps";

export type YouAre = "p1" | "p2";
export type NetMode = "ranked" | "room";

// ---- Server → Client messages -------------------------------------------------
export type ServerMsg =
  | { t: "waiting" }
  | {
      t: "matched";
      oppName: string;
      youAre: YouAre;
      bestOf: number;
      /** Opaque token used to resume THIS match within the 30s grace window. */
      reconnectToken?: string;
    }
  | { t: "chant"; round: number; deadline: number }
  | { t: "reveal_phase" }
  | {
      t: "resolve";
      p1move: RpsMove;
      p2move: RpsMove;
      p1turns: number;
      p2turns: number;
      roundWinner: YouAre | "draw";
      scores: { p1: number; p2: number };
      /** Command the throw winner landed (server-validated), null on draw. */
      cmd?: string | null;
      /** HP the landed command removed (0 on draw / roost). */
      damage?: number;
      p1hp?: number;
      p2hp?: number;
      p1crow?: number;
      p2crow?: number;
    }
  | { t: "matchOver"; winner: YouAre | "draw"; eloDelta: number; newElo: number }
  | { t: "oppLeft" }
  | { t: "error"; msg: string }
  | { t: "pong" };

export type NetStatus = "idle" | "connecting" | "waiting" | "matched" | "reconnecting" | "closed" | "error";

interface PhantomProvider {
  isPhantom?: boolean;
  publicKey?: { toString(): string };
  connect(): Promise<{ publicKey: { toString(): string } }>;
  signMessage(msg: Uint8Array, enc?: string): Promise<{ signature: Uint8Array }>;
}

function phantom(): PhantomProvider | null {
  const p = (window as unknown as { solana?: PhantomProvider }).solana;
  return p && p.isPhantom ? p : null;
}

const toHex = (buf: ArrayBuffer): string =>
  Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const toB64 = (bytes: Uint8Array): string => {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
};

/** nonce = 16 random bytes as lowercase hex. */
export function makeNonce(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return toHex(b.buffer);
}

/** Commit hash = lowercaseHex(SHA-256(move + ":" + nonce)). */
export async function commitHash(move: RpsMove, nonce: string): Promise<string> {
  const data = new TextEncoder().encode(`${move}:${nonce}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return toHex(digest);
}

export interface WalletLink {
  wallet: string;
  sig: string;
  nonce: string;
}

/** Wallet-nonce route — MUST match server/src/index.ts ("/api/wallet/nonce"). */
export const WALLET_NONCE_PATH = "/api/wallet/nonce";

/** Fetches a server nonce, signs it with Phantom, returns the link payload. */
export async function linkWallet(): Promise<WalletLink> {
  const p = phantom();
  if (!p) throw new Error("No Solana wallet found (install Phantom).");
  const { publicKey } = await p.connect();
  const wallet = publicKey.toString();
  const res = await fetch(`${apiBase()}${WALLET_NONCE_PATH}?wallet=${encodeURIComponent(wallet)}`);
  if (!res.ok) throw new Error("Could not get a nonce from the server.");
  const { nonce } = (await res.json()) as { nonce: string };
  const signed = await p.signMessage(new TextEncoder().encode(nonce), "utf8");
  return { wallet, sig: toB64(signed.signature), nonce };
}

export function hasWallet(): boolean {
  return phantom() !== null;
}

export interface HelloOpts {
  name: string;
  mode: NetMode;
  room?: string;
  /** True when this client is CREATING the room (server rate-limits creates). */
  create?: boolean;
  wallet?: WalletLink;
}

/** No pong/message for this long while connected → treat the link as dead. */
const STALL_MS = 25_000;

export class NetClient {
  status: NetStatus = "idle";
  onMessage: (m: ServerMsg) => void = () => {};
  onStatus: (s: NetStatus, detail?: string) => void = () => {};

  private ws: WebSocket | null = null;
  private hello: HelloOpts | null = null;
  private pingTimer: number | null = null;
  private reconnectDeadline = 0;
  private intentionalClose = false;
  /** True once ANY socket reached OPEN since connect() — the 30s reconnect
   *  grace only applies after that; an initial dial that never succeeds fails
   *  fast instead of silently retrying for ~35s. */
  private everConnected = false;
  /** Match-resume token from the server's `matched` message; redials carry it
   *  as ?reconnect= so we resume OUR match instead of re-entering matchmaking. */
  private reconnectToken: string | null = null;
  private lastActivity = 0;

  connect(opts: HelloOpts): void {
    this.hello = opts;
    this.intentionalClose = false;
    this.everConnected = false;
    this.reconnectToken = null;
    this.reconnectDeadline = 0;
    this.open();
  }

  /** /ws query params — the Worker routes purely on these (server index.ts). */
  private wsParams(): Record<string, string> {
    if (this.everConnected && this.reconnectToken) {
      return { reconnect: this.reconnectToken };
    }
    const p: Record<string, string> = {};
    if (this.hello) {
      p.mode = this.hello.mode;
      if (this.hello.mode === "room" && this.hello.room) {
        p.room = this.hello.room;
        if (this.hello.create) p.create = "1";
      }
      if (this.hello.wallet) p.wallet = this.hello.wallet.wallet;
    }
    return p;
  }

  private open(): void {
    this.setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl(this.wsParams()));
    } catch (e) {
      this.setStatus("error", (e as Error).message);
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.everConnected = true;
      this.lastActivity = Date.now();
      if (!this.hello) return;
      this.send({
        t: "hello",
        name: this.hello.name,
        mode: this.hello.mode,
        room: this.hello.room,
        wallet: this.hello.wallet?.wallet,
        sig: this.hello.wallet?.sig,
        nonce: this.hello.wallet?.nonce,
      });
      this.startPing();
    };
    ws.onmessage = (ev) => {
      this.lastActivity = Date.now();
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data as string) as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === "waiting") this.setStatus("waiting");
      if (msg.t === "matched") {
        if (msg.reconnectToken) this.reconnectToken = msg.reconnectToken;
        this.setStatus("matched");
      }
      if (msg.t === "pong") return;
      this.onMessage(msg);
    };
    ws.onerror = () => {
      this.setStatus("error", "Connection error.");
    };
    ws.onclose = () => this.handleClosed();
  }

  private handleClosed(): void {
    this.stopPing();
    if (this.intentionalClose) {
      this.setStatus("closed");
      return;
    }
    if (!this.everConnected) {
      // The handshake never succeeded — the server is unreachable. Fail fast
      // with feedback instead of burning the mid-match reconnect grace.
      this.setStatus("closed", "Could not reach the match server.");
      return;
    }
    // 30s reconnect grace (only after a connection actually existed).
    const now = Date.now();
    if (this.reconnectDeadline === 0) this.reconnectDeadline = now + 30_000;
    if (now < this.reconnectDeadline) {
      this.setStatus("reconnecting");
      window.setTimeout(() => this.open(), 1500);
    } else {
      this.reconnectDeadline = 0;
      this.setStatus("closed");
    }
  }

  /**
   * A dead link does not always fire `close` (e.g. the server never completes
   * the close handshake, or the network black-holes) — the socket then sits in
   * CLOSING forever while send() silently discards. Detected via the ping
   * watchdog; tear the socket down ourselves and run the normal close path.
   */
  private forceDisconnect(): void {
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    this.handleClosed();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (Date.now() - this.lastActivity > STALL_MS) {
        this.forceDisconnect();
        return;
      }
      this.send({ t: "ping" });
    }, 10_000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setStatus(s: NetStatus, detail?: string): void {
    this.status = s;
    if (s === "matched") this.reconnectDeadline = 0;
    this.onStatus(s, detail);
  }

  private send(obj: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  commit(hash: string): void {
    this.send({ t: "commit", hash });
  }

  /** `cmd` = the command-board move to land if this side wins the throw. */
  reveal(move: RpsMove, nonce: string, cmd?: string): void {
    this.send({ t: "reveal", move, nonce, cmd });
  }

  rematch(): void {
    this.send({ t: "rematch" });
  }

  close(): void {
    this.intentionalClose = true;
    this.stopPing();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }
}
